/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on async setState in debounced effects */
import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trophy, Search, Users, User, ExternalLink, FileText, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/hooks/usePageTitle";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAGUES = [
  { id: "oecsv", label: "ÖCSV", logo: "http://www.oecsv.org/wp-content/uploads/2019/08/Logo_OECSV.png", url: "http://www.oecsv.org", scoreLabel: "Score" },
  { id: "oecsv-bl", label: "ÖCSV Bundesliga", logo: "http://www.oecsv.org/wp-content/uploads/2019/08/Logo_OECSV.png", url: "http://www.oecsv.org", scoreLabel: "Score" },
  { id: "throwdarts", label: "Throwdarts", logo: "https://www.throwdarts.at/wp-content/uploads/2021/08/Logo-quadratisch-schwarz-final-800px.jpg", url: "https://www.throwdarts.at", scoreLabel: "CDA" },
];

export default function LeagueRankingsPage() {
  usePageTitle("Liga-Platzierungen");
  const [source, setSource] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("member_token");
    if (token) {
      axios.get(`${API}/members/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => setIsLoggedIn(true))
        .catch(() => {
          localStorage.removeItem("member_token");
          localStorage.removeItem("member");
          setIsLoggedIn(false);
        })
        .finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (checkingAuth) {
    return (
      <div className="py-24 px-4 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="py-12 md:py-24 px-4 md:px-8" data-testid="league-rankings-page-locked">
        <div className="max-w-xl mx-auto text-center">
          <div className="bg-card border border-white/10 p-8 md:p-12">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
              Nur für Mitglieder
            </h1>
            <p className="text-muted-foreground mb-8">
              Die Ligaplatzierung ist nur für eingeloggte Mitglieder verfügbar. Bitte melde dich an oder registriere dich, um Zugang zu erhalten.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/mitglied-login" data-testid="login-link"
                className="px-6 py-3 bg-primary text-primary-foreground font-semibold uppercase tracking-wider text-sm hover:bg-primary/90 transition-colors">
                Anmelden
              </Link>
              <Link to="/mitglied-registrieren" data-testid="register-link"
                className="px-6 py-3 border border-white/20 text-foreground font-semibold uppercase tracking-wider text-sm hover:bg-white/5 transition-colors">
                Registrieren
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="league-rankings-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-8 h-8 text-primary" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            Liga<span style={{ color: '#fbbf24' }}>platzierung</span>
          </h1>
        </div>
        <p className="text-base md:text-lg text-muted-foreground mb-10">Wähle eine Liga um Ergebnisse zu durchsuchen</p>

        {/* League Selector */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 mb-10">
          {LEAGUES.map(league => {
            const isActive = source === league.id;
            return (
              <button key={league.id} onClick={() => setSource(league.id)}
                className={`group flex flex-col items-center gap-3 transition-all duration-200 ${isActive ? 'scale-105' : 'opacity-60 hover:opacity-90'}`}
                data-testid={`league-select-${league.id}`}>
                <div className={`bg-white rounded-lg p-5 shadow-lg transition-shadow ${isActive ? 'ring-2 ring-primary shadow-primary/20' : 'group-hover:shadow-primary/10'}`}>
                  <img src={league.logo} alt={league.label} className="h-16 sm:h-20 w-auto object-contain" />
                </div>
                <span className={`text-xs font-bold uppercase tracking-widest transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                  {league.label}
                </span>
              </button>
            );
          })}
        </div>

        {source && <LeagueSection key={source} source={source} scoreLabel={LEAGUES.find(l => l.id === source).scoreLabel} />}
      </div>
    </div>
  );
}

function LeagueSection({ source, scoreLabel }) {
  const [teamQuery, setTeamQuery] = useState("");
  const [playerQuery, setPlayerQuery] = useState("");
  const [teamResults, setTeamResults] = useState([]);
  const [playerResults, setPlayerResults] = useState([]);
  const [teamDetail, setTeamDetail] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [showTeamDD, setShowTeamDD] = useState(false);
  const [showPlayerDD, setShowPlayerDD] = useState(false);
  const teamRef = useRef(null);
  const playerRef = useRef(null);
  const skipTeamSearch = useRef(false);
  const skipPlayerSearch = useRef(false);
  const isThrowdarts = source === "throwdarts";
  const isOepbv = source === "oepbv";

  useEffect(() => {
    axios.get(`${API}/${source}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [source]);

  useEffect(() => {
    if (skipTeamSearch.current) { skipTeamSearch.current = false; return; }
    if (teamQuery.length < 2) { setTeamResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/${source}/teams/search?q=${encodeURIComponent(teamQuery)}`)
        .then(r => { setTeamResults(r.data); setShowTeamDD(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [teamQuery, source]);

  useEffect(() => {
    if (skipPlayerSearch.current) { skipPlayerSearch.current = false; return; }
    if (playerQuery.length < 2) { setPlayerResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/${source}/players/search?q=${encodeURIComponent(playerQuery)}`)
        .then(r => { setPlayerResults(r.data); setShowPlayerDD(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [playerQuery, source]);

  useEffect(() => {
    const handleClick = (e) => {
      if (teamRef.current && !teamRef.current.contains(e.target)) setShowTeamDD(false);
      if (playerRef.current && !playerRef.current.contains(e.target)) setShowPlayerDD(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const onTeamFocus = () => {
    setPlayerQuery("");
    setPlayerResults([]);
    setPlayerDetail(null);
    if (teamResults.length > 0) setShowTeamDD(true);
  };

  const onPlayerFocus = () => {
    setTeamQuery("");
    setTeamResults([]);
    setTeamDetail(null);
    if (playerResults.length > 0) setShowPlayerDD(true);
  };

  const loadTeam = (divisionId, teamName) => {
    skipTeamSearch.current = true;
    setTeamQuery(teamName);
    setShowTeamDD(false);
    setTeamResults([]);
    setPlayerQuery("");
    setPlayerDetail(null);
    axios.get(`${API}/${source}/team/${divisionId}/${encodeURIComponent(teamName)}`)
      .then(r => { setTeamDetail(r.data); window.scrollTo({ top: 0, behavior: 'smooth' }); }).catch(() => {});
  };

  const loadPlayer = (divisionId, lastName, firstName) => {
    skipPlayerSearch.current = true;
    setPlayerQuery(`${firstName} ${lastName}`.trim());
    setShowPlayerDD(false);
    setPlayerResults([]);
    setTeamQuery("");
    setTeamDetail(null);
    axios.get(`${API}/${source}/player/${divisionId}/${encodeURIComponent(lastName)}/${encodeURIComponent(firstName)}`)
      .then(r => { setPlayerDetail(r.data); window.scrollTo({ top: 0, behavior: 'smooth' }); }).catch(() => {});
  };

  const selectTeam = (team) => loadTeam(team.division_id, team.team_name);
  const selectPlayer = (player) => loadPlayer(player.division_id, player.last_name, player.first_name);

  return (
    <div>
      {stats && (
        <div className="flex flex-col items-center gap-1 mb-8">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            {stats.regions && <span>{stats.regions.length} Regionen</span>}
            <span>{stats.divisions} Ligen</span>
            <span>{stats.teams} Teams</span>
            <span>{stats.players} Spieler</span>
          </div>
          {stats.last_changed && (
            <span className="text-xs text-muted-foreground/60">Letzte Änderung: {stats.last_changed}</span>
          )}
          {stats.last_run && (
            <span className="text-xs text-muted-foreground/60">Letztes Update: {new Date(stats.last_run).toLocaleString('de-AT')}</span>
          )}
        </div>
      )}

      {/* Search */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div ref={teamRef} className="relative" data-testid={`${source}-team-search`}>
          <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Teamname
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="z.B. DSV Niners..." value={teamQuery}
              onChange={e => { setTeamQuery(e.target.value); setTeamDetail(null); }}
              onFocus={onTeamFocus}
              className="pl-10 bg-card border-white/10 rounded-none h-11"
              data-testid={`${source}-team-input`} />
          </div>
          {showTeamDD && teamResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-white/10 shadow-xl max-h-64 overflow-y-auto">
              {teamResults.map((t, i) => (
                <button key={`${t.team_name}-${t.division_id}-${i}`} onClick={() => selectTeam(t)}
                  className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                  <p className="text-sm font-semibold">{t.team_name}</p>
                  <p className="text-xs text-muted-foreground">{t.division_name}{t.region ? ` — ${t.region}` : ''} — Rang {t.rank}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div ref={playerRef} className="relative" data-testid={`${source}-player-search`}>
          <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <User className="w-3.5 h-3.5" /> Spielername
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="z.B. Nachname..." value={playerQuery}
              onChange={e => { setPlayerQuery(e.target.value); setPlayerDetail(null); }}
              onFocus={onPlayerFocus}
              className="pl-10 bg-card border-white/10 rounded-none h-11"
              data-testid={`${source}-player-input`} />
          </div>
          {showPlayerDD && playerResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-white/10 shadow-xl max-h-64 overflow-y-auto">
              {playerResults.map((p, i) => (
                <button key={`${p.full_name}-${p.division_id}-${i}`} onClick={() => selectPlayer(p)}
                  className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                  <p className="text-sm font-semibold">{p.full_name}</p>
                  <p className="text-xs text-muted-foreground">{p.team_name} — {p.division_name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Team Detail */}
      {teamDetail && (
        <div className="space-y-6" data-testid={`${source}-team-detail`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{teamDetail.team.team_name}</h2>
              <p className="text-sm text-muted-foreground">{teamDetail.team.division_name || teamDetail.team.division_full_name}</p>
            </div>
            <div className="flex items-center gap-3">
              {teamDetail.pdf_url && <a href={teamDetail.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"><FileText className="w-3 h-3" /> PDF</a>}
              {teamDetail.source_url && <a href={teamDetail.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">OEPBV <ExternalLink className="w-3 h-3" /></a>}
              {teamDetail.team_url && <a href={teamDetail.team_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">ÖCSV <ExternalLink className="w-3 h-3" /></a>}
            </div>
          </div>
          <div className="bg-card border border-white/5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs text-muted-foreground uppercase tracking-widest">
                  <th className="px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Team</th>
                  {isThrowdarts ? (<><th className="px-4 py-3 text-center">Win</th><th className="px-4 py-3 text-center">Lost</th><th className="px-4 py-3 text-center">Spiele</th><th className="px-4 py-3 text-center">%</th></>) : isOepbv ? (<><th className="px-4 py-3 text-center">P</th><th className="px-4 py-3 text-center">S/U/N</th><th className="px-4 py-3 text-center">Score</th><th className="px-4 py-3 text-center">DIF</th></>) : (<><th className="px-4 py-3 text-center">Punkte</th><th className="px-4 py-3 text-center">Spiele</th><th className="px-4 py-3 text-center">Sätze</th></>)}
                </tr>
              </thead>
              <tbody>
                {teamDetail.standings.map(t => {
                  const isCurrent = t.team_name === teamDetail.team.team_name;
                  return (
                  <tr key={t.team_name} className={`border-b border-white/5 ${isCurrent ? 'bg-primary/10' : 'hover:bg-white/[0.02]'}`}>
                    <td className="px-4 py-2.5 font-bold" style={{ color: '#fbbf24' }}>{t.rank}</td>
                    <td className="px-4 py-2.5 font-semibold">
                      {isCurrent ? t.team_name : (
                        <button onClick={() => loadTeam(teamDetail.team.division_id, t.team_name)}
                          className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                          data-testid={`standings-team-${t.rank}`}>{t.team_name}</button>
                      )}
                    </td>
                    {isThrowdarts ? (<><td className="px-4 py-2.5 text-center">{t.wins}</td><td className="px-4 py-2.5 text-center">{t.losses}</td><td className="px-4 py-2.5 text-center">{t.games}</td><td className="px-4 py-2.5 text-center">{t.win_pct}</td></>) : isOepbv ? (<><td className="px-4 py-2.5 text-center font-bold">{t.points}</td><td className="px-4 py-2.5 text-center">{t.wins}/{t.draws}/{t.losses}</td><td className="px-4 py-2.5 text-center">{t.score}</td><td className="px-4 py-2.5 text-center" style={{ color: t.score_diff?.startsWith('+') ? '#10b981' : t.score_diff?.startsWith('-') ? '#ef4444' : undefined }}>{t.score_diff}</td></>) : (<><td className="px-4 py-2.5 text-center">{t.points_won}:{t.points_lost}</td><td className="px-4 py-2.5 text-center">{t.games_won}:{t.games_lost}</td><td className="px-4 py-2.5 text-center">{t.sets_won}:{t.sets_lost}</td></>)}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {teamDetail.players.length > 0 && (
            <div>
              <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>{isOepbv ? 'Einzelrangliste' : 'Spieler'}</h3>
              <div className="bg-card border border-white/5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-xs text-muted-foreground uppercase tracking-widest">
                      <th className="px-4 py-3 text-left">#</th>
                      <th className="px-4 py-3 text-left">Spieler</th>
                      {isThrowdarts ? (<th className="px-4 py-3 text-center">CDA</th>) : isOepbv ? (<><th className="px-4 py-3 text-center">PKT</th><th className="px-4 py-3 text-center">Eins.</th><th className="px-4 py-3 text-center">S/N</th><th className="px-4 py-3 text-center">%</th></>) : (<><th className="px-4 py-3 text-center">Punkte</th><th className="px-4 py-3 text-center">Teiln.</th><th className="px-4 py-3 text-center">Score</th></>)}
                    </tr>
                  </thead>
                  <tbody>
                    {teamDetail.players.map(p => (
                      <tr key={`${p.last_name}-${p.first_name}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-2.5 text-muted-foreground">{p.rank}</td>
                        <td className="px-4 py-2.5 font-semibold">
                          <button onClick={() => loadPlayer(teamDetail.team.division_id, p.last_name, p.first_name)}
                            className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                            data-testid={`team-player-${p.rank}`}>{p.first_name} {p.last_name}</button>
                        </td>
                        {isThrowdarts ? (<td className="px-4 py-2.5 text-center font-mono" style={{ color: '#fbbf24' }}>{p.cda_score?.toFixed(2)}</td>) : isOepbv ? (<><td className="px-4 py-2.5 text-center font-bold">{p.points}</td><td className="px-4 py-2.5 text-center">{p.appearances}</td><td className="px-4 py-2.5 text-center">{p.wins}/{p.losses}</td><td className="px-4 py-2.5 text-center font-mono" style={{ color: '#fbbf24' }}>{p.win_pct?.toFixed(1)}</td></>) : (<><td className="px-4 py-2.5 text-center">{p.points}</td><td className="px-4 py-2.5 text-center">{p.participations}</td><td className="px-4 py-2.5 text-center font-mono" style={{ color: '#fbbf24' }}>{p.score?.toFixed(2)}</td></>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Player Detail */}
      {playerDetail && (
        <div className="space-y-6" data-testid={`${source}-player-detail`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{playerDetail.player.first_name} {playerDetail.player.last_name}</h2>
              <p className="text-sm text-muted-foreground">
                {playerDetail.player.team_name ? (
                  <><button onClick={() => loadTeam(playerDetail.player.division_id, playerDetail.player.team_name)}
                    className="hover:text-primary hover:underline underline-offset-2 transition-colors"
                    data-testid="player-header-team-link">{playerDetail.player.team_name}</button>{' — '}</>
                ) : null}
                {playerDetail.player.division_name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {playerDetail.pdf_url && <a href={playerDetail.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"><FileText className="w-3 h-3" /> PDF</a>}
              {playerDetail.source_url && <a href={playerDetail.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">OEPBV <ExternalLink className="w-3 h-3" /></a>}
              {playerDetail.player_url && <a href={playerDetail.player_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:text-primary/80">ÖCSV <ExternalLink className="w-3 h-3" /></a>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Rang" value={playerDetail.player.rank} />
            {isThrowdarts ? (
              <StatCard label="CDA" value={playerDetail.player.cda_score?.toFixed(2)} highlight />
            ) : isOepbv ? (
              <><StatCard label="PKT" value={playerDetail.player.points} /><StatCard label="S/N" value={`${playerDetail.player.wins}/${playerDetail.player.losses}`} /><StatCard label="%" value={playerDetail.player.win_pct?.toFixed(1)} highlight /></>
            ) : (
              <><StatCard label="Punkte" value={playerDetail.player.points} /><StatCard label="Teilnahmen" value={playerDetail.player.participations} /><StatCard label="Score" value={playerDetail.player.score?.toFixed(2)} highlight /></>
            )}
          </div>
          {playerDetail.all_entries?.length > 1 && (
            <div>
              <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Alle Ligen</h3>
              <div className="bg-card border border-white/5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/10 text-xs text-muted-foreground uppercase tracking-widest"><th className="px-4 py-3 text-left">Liga</th>{!isOepbv && <th className="px-4 py-3 text-left">Team</th>}<th className="px-4 py-3 text-center">#</th><th className="px-4 py-3 text-center">{scoreLabel}</th></tr></thead>
                  <tbody>
                    {playerDetail.all_entries.map((e, i) => (
                      <tr key={`${e.division_id}-${e.team_name}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-2.5 text-sm">{e.division_name}</td>
                        {!isOepbv && <td className="px-4 py-2.5 font-semibold">
                          <button onClick={() => loadTeam(e.division_id, e.team_name)}
                            className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                            data-testid={`allleagues-team-${i}`}>{e.team_name}</button>
                        </td>}
                        <td className="px-4 py-2.5 text-center">{e.rank}</td>
                        <td className="px-4 py-2.5 text-center font-mono" style={{ color: '#fbbf24' }}>{isThrowdarts ? e.cda_score?.toFixed(2) : isOepbv ? e.win_pct?.toFixed(1) : e.score?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {playerDetail.division_players?.length > 0 && (
            <div>
              <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Einzelrangliste — {playerDetail.player.division_name}</h3>
              <div className="bg-card border border-white/5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/10 text-xs text-muted-foreground uppercase tracking-widest"><th className="px-4 py-3 text-left">#</th><th className="px-4 py-3 text-left">Spieler</th>{!isOepbv && <th className="px-4 py-3 text-left">Team</th>}<th className="px-4 py-3 text-center">{scoreLabel}</th></tr></thead>
                  <tbody>
                    {playerDetail.division_players.map((p, i) => {
                      const isMe = p.last_name === playerDetail.player.last_name && p.first_name === playerDetail.player.first_name;
                      return (
                        <tr key={`${p.last_name}-${p.first_name}-${p.rank}`} className={`border-b border-white/5 ${isMe ? 'bg-primary/10' : 'hover:bg-white/[0.02]'}`}>
                          <td className="px-4 py-2.5 font-bold" style={{ color: '#fbbf24' }}>{p.rank}</td>
                          <td className="px-4 py-2.5 font-semibold">
                            {isMe ? <span>{p.first_name} {p.last_name}</span> : (
                              <button onClick={() => loadPlayer(playerDetail.player.division_id, p.last_name, p.first_name)}
                                className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                                data-testid={`ranking-player-${p.rank}`}>{p.first_name} {p.last_name}</button>
                            )}
                          </td>
                          {!isOepbv && <td className="px-4 py-2.5 text-muted-foreground">
                            <button onClick={() => loadTeam(playerDetail.player.division_id, p.team_name)}
                              className="hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                              data-testid={`ranking-team-${p.rank}`}>{p.team_name}</button>
                          </td>}
                          <td className="px-4 py-2.5 text-center font-mono" style={{ color: '#fbbf24' }}>{isThrowdarts ? p.cda_score?.toFixed(2) : isOepbv ? p.win_pct?.toFixed(1) : p.score?.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!teamDetail && !playerDetail && (
        <div className="text-center py-10 text-muted-foreground text-sm">
          <p>Suche nach einem Team oder Spieler um die Ergebnisse anzuzeigen.</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className="bg-card border border-white/5 p-4 text-center">
      <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{label}</p>
      <p className="text-xl font-bold" style={highlight ? { color: '#fbbf24', fontFamily: 'Oswald' } : { fontFamily: 'Oswald' }}>{value}</p>
    </div>
  );
}
